/*
  PDP purchase options -- the thing that makes the band do something.

  THE STRUCTURAL PROBLEM IT EXISTS FOR. The One Time / Subscribe radios render
  in a `custom_liquid` block that is a SIBLING of `buy_buttons`, so they are
  outside the `<form action="/cart/add">` that `product-form.js` posts. A radio
  outside a form is not in its FormData, so the choice could never reach the
  cart on its own. This element writes the selected plan into the final
  `FormData` payload at the form's `formdata` event boundary.

  AND THE THEME IS NOT THE ONLY WRITER OF THAT FIELD. Measured 2026-08-12, after
  the `appstle-subscription` app embed was enabled: Appstle injects its OWN
  purchase-option widget into `.product-form__buttons`, inside the same form,
  carrying its own `selling_plan` radio group --

      appstle_selling_plan_label_10  value=""            checked
      appstle_selling_plan_label_20  value="3482943700"

  -- so the form held THREE fields named `selling_plan`. `FormData` sends every
  one of them and Shopify takes the last, which was Appstle's empty one. The
  first build of this file bound the plan perfectly, printed the right price,
  and added a one-time line, because a fourth writer had quietly cancelled it.

  SO THIS ELEMENT NORMALISES THE FIELD instead of just setting it. The
  controller disables every app-owned `selling_plan` field. A `formdata`
  listener adds exactly one `selling_plan` value at the payload boundary.

  Note that the app widget is functional and DUPLICATES this band visually;
  `pure-product.css` suppresses its chrome. See the note there.

  ONE SIGNAL DRIVES BOTH VISIBLE AND SUBMITTED STATE. The selected radio drives
  both the formdata value and `data-pure-option` on `<product-info>`, and the
  price shown in the add-to-cart button is CSS keyed off that same attribute
  (`pure-product.css`). There is no second path that could paint one price and
  post another -- which is the defect this band was scoped around, in a
  different costume.

  DIRECTION OF FAILURE. No script, a script error, an unbound band, or a variant
  Shopify has not allocated the plan to leaves the payload without a selling
  plan, so it lands on "one-time purchase" rather than an unintended subscription.

  SECTION-SCOPED AND IDEMPOTENT by construction: a custom element connects when
  Shopify re-renders the section and disconnects when it is removed, so there is
  no `shopify:section:load` wiring to leak, and the listeners are on nodes this
  element owns or on a node it un-listens in `disconnectedCallback`.

  NO MOTION, so nothing for `prefers-reduced-motion` to reduce. The controls are
  real radios in a `role="radiogroup"`, so arrow keys, Tab and state
  announcement are the platform's and not re-implemented here.
*/
if (!customElements.get('pure-purchase-options')) {
  customElements.define(
    'pure-purchase-options',
    class PurePurchaseOptions extends HTMLElement {
      connectedCallback() {
        // The documented kill switch. `bind_selling_plan` false renders the
        // element without this attribute and the band stays exactly as inert as
        // it was before any of this existed.
        if (this.dataset.bind !== 'true') return;

        this.info = this.closest('product-info');
        if (!this.info) return;

        // `input[name="id"]` is Dawn's variant field. It is the variant reading
        // that cannot drift from what will be posted, because it IS what will be
        // posted -- and `product-info.js` dispatches a `change` on it on every
        // variant change, so it doubles as the variant-change signal without a
        // pub/sub dependency or an assumption about swap ordering.
        this.variantInput = this.info.querySelector('form[action*="/cart/add"] input[name="id"]');
        if (!this.variantInput) return;

        this.form = this.variantInput.closest('form');
        if (!this.form) return;

        this.plans = this.readPlans();
        this.subscribeRadio = this.querySelector('input[type="radio"][value="subscribe"]');
        this.onetimeRadio = this.querySelector('input[type="radio"][value="onetime"]');
        this.frequencyField = this.querySelector('[data-pure-frequency]');
        this.frequencyRadios = Array.from(this.querySelectorAll('.pure-selling-plan__input'));
        if (!this.subscribeRadio || !this.onetimeRadio) return;

        // Liquid renders the initial choice from Shopify's native selling_plan
        // URL parameter. Adopt those radios without changing the first paint.
        this.onChange = (event) => {
          // Appstle listens for generic radio changes at document level and
          // otherwise resets its hidden, first-plan-only widget after a customer
          // chooses a different frequency in this theme-owned control.
          event.stopPropagation();
          this.apply();
        };
        this.onVariantChange = () => this.apply();
        this.addEventListener('change', this.onChange);
        this.variantInput.addEventListener('change', this.onVariantChange);

        // THE APP WIDGET ARRIVES AFTER WE DO. Appstle injects its own
        // `selling_plan` radios into the form from its embed script, well after
        // this element connects, and re-injects them on variant change. Without
        // this, picking Subscribe before the app loads would leave our field
        // carrying the plan and the app's empty radio arriving afterwards to
        // cancel it -- a race whose losing side is silent and looks fine.
        //
        // Watching the FORM rather than the document keeps it cheap. The
        // callback re-applies only when the form would post the WRONG thing, so
        // our own click -- and any re-render the app does in response to it --
        // settles instead of ping-ponging: the second pass finds the field
        // already correct and does nothing.
        if (this.form && window.MutationObserver) {
          this.observer = new MutationObserver(() => {
            if (this.applying || this.isSatisfied()) return;
            this.apply();
          });
          this.observer.observe(this.form, {
            childList: true,
            attributes: true,
            attributeFilter: ['disabled'],
            subtree: true,
          });
        }

        // AND NORMALISE AGAIN AT SUBMIT, in the capture phase, which is the one
        // moment that cannot be raced. Everything above is eventually
        // consistent: the app re-renders its radios on variant change, the
        // observer is a microtask behind it, and a visitor who changes variant
        // and immediately hits Add to Cart can post in the gap.
        //
        // THIS IS NOT HYPOTHETICAL. `_cart-plan-gate.mjs` over all 68 products
        // failed exactly two of them -- `hydrate` and `buzzin-thc-seltzer`, the
        // 12- and 16-variant Beverage products, where the app's re-render is
        // heaviest and the gap widest. Both passed the pre-submit assertion and
        // then added a line with no allocation.
        //
        // Capture on the form runs before `product-form.js`'s own bubble-phase
        // submit listener on the same element, so `apply()` has finished before
        // `new FormData(this.form)` is built.
        if (this.form) {
          this.onSubmit = () => this.apply();
          this.form.addEventListener('submit', this.onSubmit, true);

          // `product-form.js` posts `new FormData(form)`. The formdata event is
          // the last synchronous boundary before that payload is sent, so it
          // guarantees one authoritative plan even if an app changes its own
          // hidden controls during submit handling.
          this.onFormData = (event) => {
            const variantId = this.currentVariantId();
            const plan = this.subscribeRadio.checked ? this.planForCurrentVariant() : '';
            if (variantId) event.formData.set('id', variantId);
            if (plan) event.formData.set('selling_plan', plan);
            else event.formData.delete('selling_plan');
          };
          this.form.addEventListener('formdata', this.onFormData);
        }

        this.apply();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.form && this.onSubmit) this.form.removeEventListener('submit', this.onSubmit, true);
        if (this.form && this.onFormData) this.form.removeEventListener('formdata', this.onFormData);
        if (!this.onChange) return;
        this.removeEventListener('change', this.onChange);
        this.variantInput.removeEventListener('change', this.onVariantChange);
        // Leave nothing behind that outlives the element: a stale
        // `data-pure-option` would keep painting a subscription price beside a
        // form that no longer has a plan bound to it.
        this.clear();
      }

      // `[[variantId, [planId, ...]], ...]`, not an object -- Liquid's lexer is not
      // string-aware and cannot emit a `}` from a variable, so the template
      // side has no braces in it at all. See the note in the snippet.
      readPlans() {
        const node = this.querySelector('script[type="application/json"][data-pure-selling-plans]');
        if (!node) return new Map();
        try {
          const pairs = JSON.parse(node.textContent);
          return new Map(
            Array.isArray(pairs)
              ? pairs.map(([variantId, planIds]) => [
                  String(variantId),
                  Array.isArray(planIds) ? planIds.map(String) : [],
                ])
              : []
          );
        } catch {
          return new Map();
        }
      }

      // The plan for the variant that is currently selected, NOT a
      // product-level constant. Shopify allocates plans per variant and rejects
      // `/cart/add` for a plan the variant does not carry.
      currentVariantId() {
        const node = this.info.querySelector('variant-selects [data-selected-variant]');
        try {
          const variant = JSON.parse(node?.textContent || 'null');
          if (variant?.id) return String(variant.id);
        } catch {
          // Fall through to Dawn's form value when the JSON is unavailable.
        }
        return this.variantInput.value ? String(this.variantInput.value) : '';
      }

      plansForCurrentVariant() {
        return this.plans.get(this.currentVariantId()) || [];
      }

      planForCurrentVariant() {
        const plans = this.plansForCurrentVariant();
        const selected = this.frequencyRadios.find((radio) => radio.checked && plans.includes(String(radio.value)));
        return selected ? String(selected.value) : plans[0] || '';
      }

      updateFrequencyPicker() {
        const plans = this.plansForCurrentVariant();
        const subscribed = this.subscribeRadio.checked && plans.length > 0;

        for (const radio of this.frequencyRadios) {
          const available = plans.includes(String(radio.value));
          radio.disabled = !available;
          const option = radio.closest('[data-pure-selling-plan-option]');
          if (option) option.hidden = !available;
        }

        if (plans.length && !this.frequencyRadios.some((radio) => radio.checked && !radio.disabled)) {
          const firstAvailable = this.frequencyRadios.find((radio) => !radio.disabled);
          if (firstAvailable) firstAvailable.checked = true;
        }

        if (this.frequencyField) this.frequencyField.hidden = !subscribed;
      }

      // Every `selling_plan` field in the form that this element did not
      // render. Selected by NAME rather than by any `appstle` class, so a
      // second app, or a rename inside this one, is handled by the same code.
      foreignFields() {
        if (!this.form) return [];
        return Array.from(this.form.querySelectorAll('[name="selling_plan"]'));
      }

      isSatisfied() {
        const variantId = this.currentVariantId();
        const plan = this.planForCurrentVariant();
        const subscribed = this.subscribeRadio.checked && !!plan;
        if (variantId && this.variantInput.value !== variantId) return false;
        if (this.foreignFields().some((field) => !field.disabled)) return false;
        return this.info.dataset.pureOption === (subscribed ? 'subscribe' : 'onetime');
      }

      apply() {
        if (this.applying) return;
        this.applying = true;
        try { this.applyOnce(); } finally { this.applying = false; }
      }

      applyOnce() {
        // Appstle clears Dawn's hidden variant id on some multi-option products
        // while injecting its own widget. The selected-variant JSON remains the
        // authoritative result of Dawn's option resolution, so restore the form
        // field here; `formdata` repeats this at the final payload boundary.
        const variantId = this.currentVariantId();
        if (variantId && this.variantInput.value !== variantId) this.variantInput.value = variantId;

        this.updateFrequencyPicker();
        const plan = this.planForCurrentVariant();

        // A variant with no allocation cannot be subscribed to at all. Say so
        // rather than leaving a control that posts an id Shopify will refuse:
        // the radio goes disabled and the choice falls back to one-time. No
        // product in this store hits this today (`_cart-plan-gate.mjs` gates
        // that across every product with a group), so it is a guard, not a
        // behaviour.
        this.subscribeRadio.disabled = !plan;
        if (!plan && this.subscribeRadio.checked) this.onetimeRadio.checked = true;

        const subscribed = this.subscribeRadio.checked && !!plan;
        const foreign = this.foreignFields();

        // A foreign field that is not part of a radio group always submits, so
        // it would collide with whatever we choose no matter which side wins.
        // Disabled unconditionally; it is the app's field, but it is our form.
        for (const el of foreign) if (el.type !== 'radio') el.disabled = true;

        // The app's widget is visually suppressed and only exposes its default
        // plan as a radio. Letting it remain a second form writer means every
        // non-default frequency can be silently changed back to the first plan.
        // The theme owns this form control, while Appstle still owns the selling
        // plan and post-checkout subscription service.
        for (const el of foreign) {
          el.disabled = true;
          if (el.type === 'radio') el.checked = false;
        }
        this.info.dataset.pureOption = subscribed ? 'subscribe' : 'onetime';
      }

      clear() {
        if (this.info) this.info.dataset.pureOption = 'onetime';
        if (this.frequencyField) this.frequencyField.hidden = true;
      }
    }
  );
}
